import React, { useState } from 'react';
import { Upload, FileText, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

interface FileInfo {
  name: string;
  type: string;
  duration: number;
}

interface FileUploadProps {
  setFileInfo: (fileInfo: FileInfo | null) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({ setFileInfo }) => {
  const [file, setFile] = useState<File | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { user, balance, deductCredit } = useAuth();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setFile(selectedFile);

      const audio = new Audio(URL.createObjectURL(selectedFile));
      audio.addEventListener('loadedmetadata', () => {
        const audioDuration = audio.duration;
        setDuration(audioDuration);
        setFileInfo({
          name: selectedFile.name,
          type: selectedFile.type,
          duration: audioDuration
        });
      });
    }
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const calculatePrice = (seconds: number) => {
    const minutes = seconds / 60;
    const price = Math.max(5, minutes * 1.5);
    return parseFloat(price.toFixed(2));
  };

  const handleUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !duration || !user) return;

    const price = calculatePrice(duration);

    if (balance < price) {
      setError(
        `Insufficient credit. You need £${price.toFixed(2)} to process this file.`
      );
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('fileName', file.name);
    formData.append('fileType', file.type);
    formData.append('duration', duration.toString());
    formData.append('price', price.toString());
    formData.append('userId', user.id);
    formData.append('userName', user.username);
    formData.append('userEmail', user.email);
    formData.append('userFirstname', user.firstname || '');
    formData.append('userLastname', user.lastname || '');

    try {
      const response = await fetch(import.meta.env.VITE_WEBHOOK_URL, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload file');
      }

      const deductionSuccessful = await deductCredit(price);
      if (!deductionSuccessful) {
        throw new Error('Failed to deduct credit');
      }

      setSuccess(
        `Thanks, your transcription is being worked on and will be with you shortly. £${price.toFixed(
          2
        )} has been deducted from your balance.`
      );
      setFileInfo(null);
    } catch (error) {
      setError(
        'An error occurred while processing your request. Please try again.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto">
      <h1 className="text-3xl font-bold mb-6">Transcribe Audio</h1>
      <form
        onSubmit={handleUpload}
        className="bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4"
      >
        <div className="mb-4">
          <label
            className="block text-gray-700 text-sm font-bold mb-2"
            htmlFor="audio-file"
          >
            Select an audio file
          </label>
          <input
            type="file"
            id="audio-file"
            accept="audio/*"
            onChange={handleFileChange}
            className="hidden"
          />
          <label
            htmlFor="audio-file"
            className="flex items-center justify-center w-full px-4 py-2 bg-teal-500 text-white rounded-lg cursor-pointer hover:bg-teal-600"
          >
            <Upload className="mr-2" />
            Choose File
          </label>
        </div>
        {file && duration && (
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-2">File Information:</h3>
            <p className="flex items-center">
              <FileText className="w-5 h-5 mr-2" /> Name: {file.name}
            </p>
            <p className="flex items-center">
              <FileText className="w-5 h-5 mr-2" /> Type: {file.type}
            </p>
            <p className="flex items-center">
              <Clock className="w-5 h-5 mr-2" /> Duration:{' '}
              {formatDuration(duration)}
            </p>
            <p className="mt-4 text-xl font-bold">
              Price: £{calculatePrice(duration).toFixed(2)}
            </p>
          </div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            <p>{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
            <p>{success}</p>
          </div>
        )}
        <button
          type="submit"
          disabled={!file || isLoading || (duration && calculatePrice(duration) > balance)}
          className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
            !file || isLoading || (duration && calculatePrice(duration) > balance)
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500'
          }`}
        >
          {isLoading ? 'Uploading...' : 'Upload'}
        </button>
      </form>
    </div>
  );
};

export default FileUpload;